import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaTruck,
  FaRedo,
  FaCalendarAlt,
  FaDollarSign,
  FaUser,
  FaBox,
} from "react-icons/fa";
import { Bold } from "lucide-react";

const OrderCard = ({ order, paymentStatus, fulfillmentStatus }) => {
  const product = order.items[0];
  const orderDate = new Date(order.createDate).toLocaleDateString();
  const deliveryDate = new Date(order.updateDate).toLocaleDateString();
  const navigate = useNavigate();

  // Determine status type (color + message)
  const getStatusUI = () => {
    const payment = order.paymentStatus;
    const fulfillment = order.fulfillmentStatus;

    if (payment === "PAID" && fulfillment === "DELIVERED") {
      return {
        iconColor: "text-green-500",
        message: `Delivered on ${deliveryDate}`,
        badge: "Delivered",
        badgeColor: "bg-green-100 text-green-800 border-green-200",
      };
    } else if (
      payment === "AWAITING_PAYMENT" &&
      fulfillment === "AWAITING_PROCESSING"
    ) {
      return {
        iconColor: "text-yellow-500",
        message: `Processing`,
        badge: "Processing",
        badgeColor: "bg-yellow-100 text-yellow-800 border-yellow-200",
      };
    } else if (payment === "PAID" && fulfillment === "SHIPPED") {
      return {
        iconColor: "text-blue-500",
        message: `Shipped`,
        badge: "Shipped",
        badgeColor: "bg-blue-100 text-blue-800 border-blue-200",
      };
    } else if (payment === "AWAITING_PAYMENT" && fulfillment === "SHIPPED") {
      return {
        iconColor: "text-orange-500",
        message: `Awaiting Payment`,
        badge: "Awaiting Payment",
        badgeColor: "bg-orange-100 text-orange-800 border-orange-200",
      };
    } else if (payment === "CANCELLED" && fulfillment === "WILL_NOT_DELIVER") {
      return {
        iconColor: "text-red-500",
        message: `Cancelled`,
        badge: "Cancelled",
        badgeColor: "bg-red-100 text-red-800 border-red-200",
      };
    } else {
      return {
        iconColor: "text-gray-400",
        message: "Status Unknown",
        badge: "Unknown",
        badgeColor: "bg-gray-100 text-gray-600 border-gray-200",
      };
    }
  };

  const statusUI = getStatusUI();

  return (
    <div
      className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-300 mb-6"
      style={{ width: "100%" }}
    >
      {/* Header with gradient background */}
      <div className="bg-gradient-to-b from-gray-100 to-gray-200 px-6 py-4 border-b border-gray-300">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-white border border-gray-200 p-2 rounded-lg">
              <FaCalendarAlt className="text-gray-600" />
            </div>
            <div>
              <div className="text-xs text-lime-600 font-medium">ORDER #</div>
              <div className="text-s text-sm font-semibold text-gray-800">
                {order.id}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="bg-white border border-gray-200 p-2 rounded-lg">
              <FaBox className="text-gray-600" />
            </div>
            <div>
              <div className="text-xs text-lime-600 font-medium">
                ORDER PLACED
              </div>
              <div className="text-sm font-semibold text-gray-800">
                {orderDate}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="bg-white border border-gray-200 p-2 rounded-lg">
              <FaDollarSign className="text-gray-600" />
            </div>
            <div>
              <div className="text-xs text-lime-600 font-medium">TOTAL</div>
              <div className="text-sm font-semibold text-gray-800">
                ${order.total.toFixed(2)}
              </div>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-3">
            <div className="bg-white border border-gray-200 p-2 rounded-lg">
              <FaUser className="text-gray-600" />
            </div>
            <div>
              <div className="text-xs text-lime-600 font-medium">SHIP TO</div>
              <div className="text-sm font-semibold text-gray-800 truncate max-w-[120px]">
                {order.billingPerson?.name || "Not Available"}
              </div>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-3">
            <div className="bg-white border border-gray-200 p-2 rounded-lg">
              <FaUser className="text-gray-600" />
            </div>
            <div>
              <div className="text-xs text-lime-600 font-medium">Email</div>
              <div className="text-sm font-semibold text-gray-800 truncate max-w-[120px]">
                {order.email || "Not Available"}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="bg-white border border-gray-200 p-2 rounded-lg">
              <FaBox className="text-gray-600" />
            </div>
            <div>
              <div className="text-xs text-lime-600 font-medium">Payment</div>
              <div className="text-sm font-semibold text-gray-800">
                {order.paymentMethod}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Info */}
      <div className="p-6">
        <div className="flex flex-col md:flex-row gap-6">
          <img
            src={product.imageUrl || "/assets/No-img.png.png"}
            alt={product.name}
            className="w-full md:w-32 h-32 object-cover rounded-xl border border-gray-200 shadow-sm"
          />
          <div className="flex-1">
            <div className="flex flex-col md:flex-row justify-between gap-4">
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">
                  {product.name}
                </h3>

                {/* Status Badge */}
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusUI.badgeColor} mb-3`}
                >
                  <FaTruck className={`mr-2 ${statusUI.iconColor}`} />{" "}
                  {statusUI.badge}
                </span>

                {/* Status Message */}
                <p className="text-sm text-gray-600 flex items-center gap-2 mb-2">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      statusUI.iconColor === "text-green-500"
                        ? "bg-green-500"
                        : statusUI.iconColor === "text-yellow-500"
                        ? "bg-yellow-500"
                        : statusUI.iconColor === "text-blue-500"
                        ? "bg-blue-500"
                        : statusUI.iconColor === "text-orange-500"
                        ? "bg-orange-500"
                        : statusUI.iconColor === "text-red-500"
                        ? "bg-red-500"
                        : "bg-gray-400"
                    }`}
                  ></span>
                  {statusUI.message}
                </p>

                {/* Quantity */}
                <p className="text-sm text-gray-500">
                  Quantity:{" "}
                  <span className="font-medium">{product.quantity}</span>
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/order-details/${order.id}`}
                  state={{ order }}
                  // className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors duration-200 text-center"
                  className="px-3 py-1 text-sm font-medium flex items-center justify-center gap-2 border border-gray-300 bg-gradient-to-r from-gray-300 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-900 font-bold py-3 rounded-md shadow-md transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg"
                  style={{
                    height: "40px",
                    lineHeight: "1",
                    text: Bold,
                  }}
                >
                  View Details
                </Link>
                <button
                  onClick={() =>
                    navigate(`/product/${product.id}`, { state: { product } })
                  }
                  // className="px-4 py-2 bg-[#82b016] hover:bg-[#6fa010] text-white font-medium rounded-lg flex items-center justify-center gap-2 shadow transition-all duration-200"
                  className="px-3 py-1 text-white text-sm font-medium rounded-md flex items-center justify-center gap-2 bg-gradient-to-r from-[#82b016] to-green-600 hover:from-[#6fa010] hover:to-green-700 text-white font-bold py-3 rounded-lg shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl"
                  style={{
                    height: "40px",
                    lineHeight: "1",
                  }}
                >
                  <FaRedo className="text-white" /> Buy Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderCard;
